import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ShareNames } from '../../models/ServicePresets';
import { GlobalRouteNames, LocalStorageItems, ModalVariants, QueryParams } from '../../models/Common';
import { formatAccountType, getCardIconType, getSearchParamValue, getErrorMessage } from '../../utils';
import { ChangeServiceTypes, AvailableServiceTypes } from '../../models/Accounts';
import './ChangeService.scss';
import { UserSubtitle } from '../../components/commons/UserSubtitle';
import { ChangeServiceTabs } from '../../components/changeServiceParts/ChangeServiceTabs';
import { ChangeServiceCardList } from '../../components/changeServiceParts/ChangeServiceCardList';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { userInfoApi } from '../../services/apiServices/user/userInfo';
import { useAvailableServices } from '../../hooks/useAvailableServices';
import { useUserAddress } from '../../hooks/useUserAddress';
import { useAccountInfo } from '../../hooks/useAccountInfo';
import { modalSlice } from '../../store/reducers/ModalSlice';
import { initialState } from '../../store/reducers/UserInfoSlice';

export const ChangeService = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const [availableServiceGroups, setAvailableServiceGroups] = useState<ChangeServiceTypes[]>([]);
  const [tabValue, setTabValue] = useState<number>(0);
  const controlledAccount = localStorage.getItem(LocalStorageItems.ControlledAccount);
  const { useGetUserInfoQuery, useGetUserInfoMultiAccQuery } = userInfoApi;
  const { data: userInfoState, isSuccess: isUserInfoDownloaded } = controlledAccount
    ? useGetUserInfoMultiAccQuery(
        { subAccountId: JSON.parse(controlledAccount).UserID },
        { refetchOnMountOrArgChange: true },
      )
    : useGetUserInfoQuery('', { refetchOnMountOrArgChange: true });
  const { userAddress } = useUserAddress(userInfoState || initialState.userInfoState, selectedLanguage);
  const accountType = formatAccountType(getSearchParamValue(QueryParams.AccountType, search));
  const accountId = getSearchParamValue(QueryParams.AccountId, search);
  const {
    accountInfo,
    isError: isAccountError,
    error: accountError,
    servicePresets,
  } = useAccountInfo(accountId, accountType);
  const { types: typedServices } = useAvailableServices(accountId, accountType);

  const getModifiedServices = (services: AvailableServiceTypes[], currentServiceId: number): ChangeServiceTypes[] => {
    const servicesWithoutZeroPrice = services.map((service) => {
      return {
        ...service,
        Services: service.Services.filter((s) => s.Price),
      };
    });

    return servicesWithoutZeroPrice.map((service) => {
      const servicesType = service.TypeId >= 1000 ? 'inet' : 'union';
      return {
        ...service,
        isActive: !!service.Services.find((item) => item.id === currentServiceId),
        Services: service.Services.map((item) => {
          return {
            ...item,
            isAvailableForConnection: !!item.isAllowUserChange,
            icon: getCardIconType(accountType, item.Type?.id),
            isCurrent: item.id === currentServiceId,
            isPrepaid: !!servicePresets?.Prepaids?.some((prepaid) => prepaid.Service.id === item.id),
            isCashback: !servicePresets?.Shares?.[ShareNames.BonusPrepaids]?.[
              `notavailiable_${servicesType}_services`
            ].includes(item.id),
          };
        }),
      };
    });
  };

  useEffect(() => {
    if (accountError && isAccountError) {
      const message = getErrorMessage(accountError);
      history.replace({ pathname: GlobalRouteNames.UnknownError, state: { message } });
    }
  }, [accountError, isAccountError]);

  useEffect(() => {
    setAvailableServiceGroups([]);
    if (servicePresets && typedServices.length && accountInfo.serviceId) {
      const modifiedServices = getModifiedServices(typedServices, accountInfo.serviceId);
      setAvailableServiceGroups((prevState) => {
        const availableServices = [...prevState, ...modifiedServices];
        const typedTab = availableServices.findIndex((i) => i.isActive);
        if (typedTab + 1) {
          setTabValue(typedTab);
        }
        return availableServices;
      });
    }
  }, [servicePresets, typedServices, accountInfo]);

  const handleSetTabValue = (event: React.SyntheticEvent | SelectChangeEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleChange = (serviceId: string | number, isDaily: boolean, isCurrent: boolean) => {
    history.push({
      pathname: isDaily ? GlobalRouteNames.DailyServicePayment : GlobalRouteNames.ServicePayment,
      search: `?${QueryParams.AccountId}=${accountId}&${QueryParams.AccountType}=${accountType}&${
        QueryParams.ServiceId
      }=${serviceId}&${QueryParams.IsChangeService}=${!isCurrent}`,
    });
  };

  const checkRouterBeforeChange = (serviceId: string | number, isDaily: boolean, isCurrent: boolean) => {
    if (
      !isCurrent &&
      !localStorage.getItem(LocalStorageItems.RouterCapabilityChecked) &&
      !!availableServiceGroups[tabValue]?.Services?.find((s) => s.id === +serviceId)?.IsGigabitRouterRequired
    ) {
      dispatch(
        openModal({
          modalVariants: ModalVariants.CheckRouter,
          content: {
            isDaily,
            title: t('common.attention'),
            search: `?${QueryParams.AccountId}=${accountId}&${QueryParams.AccountType}=${accountType}&${
              QueryParams.ServiceId
            }=${serviceId}&${QueryParams.IsChangeService}=${!isCurrent}`,
          },
        }),
      );
    } else {
      handleChange(serviceId, isDaily, isCurrent);
    }
  };

  const handleCallback = (serviceName: string, serviceAnalyticsName) => {
    const site = `Сайт: ${process.env.REACT_APP_NAME || ''}.`;
    const serviceRequest = `Запрос на пакет: ${serviceName}.`;
    const agreement = isUserInfoDownloaded && `№ договора: ${userInfoState.id}.`;
    const address =
      isUserInfoDownloaded &&
      `Домашний адрес: ул. ${userInfoState?.Addresses[0].Street.NameRu} дом ${userInfoState?.Addresses[0].House}.`;
    dispatch(
      openModal({
        modalVariants: ModalVariants.Callback,
        content: {
          service: serviceName,
          title: t('changeService.serviceActivationRequest'),
          comment: `${site} ${serviceRequest} ${agreement} ${address}`,
          serviceAnalyticsName,
        },
      }),
    );
  };

  return (
    <div className="change-service page">
      <div className="change-service__container container">
        {!controlledAccount ? (
          <h1 className="change-service__title">{t('changeService.changeService')}</h1>
        ) : (
          <h1 className="change-service__title">
            {t('changeService.changeService')}
            <UserSubtitle address={userAddress.trim()} userId={userInfoState?.id ?? initialState.userInfoState.id} />
          </h1>
        )}
        {availableServiceGroups && availableServiceGroups.length > 1 ? (
          <div className="change-service__tabs-container">
            <ChangeServiceTabs
              tabValue={tabValue}
              handleSetTabValue={handleSetTabValue}
              availableServiceGroups={availableServiceGroups}
              handleChange={checkRouterBeforeChange}
              handleCallback={handleCallback}
              canChange={!!userInfoState?.Permissions?.Services?.Change}
              canActivate={!!userInfoState?.Permissions?.Services?.Activate}
            />
          </div>
        ) : (
          <div className="change-service__tabs-container">
            <ChangeServiceCardList
              availableServices={availableServiceGroups}
              handleChange={checkRouterBeforeChange}
              handleCallback={handleCallback}
              canChange={!!userInfoState?.Permissions?.Services?.Change}
              canActivate={!!userInfoState?.Permissions?.Services?.Activate}
            />
          </div>
        )}
      </div>
    </div>
  );
};
